import * as React from 'react';
import { connect } from "react-redux";
import Alert from 'react-s-alert';
import { bindActionCreators } from "redux";
import { SubPageLayout } from "../../../components/layouts/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { FC, useEffect, useMemo } from 'react';
import { ChangeOptions, Form, ModelAction, actions } from 'react-redux-form';
import * as TrialGroupItemActions from '../../../actions/trialGroupItem';
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from '../../../dtos/Tmd.dtos';
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { ITrialGroupItemReduxFormState } from "../../../reducers/reactReduxForms/trialGroupItem";

import './TrialGroupItemCreateVersion.scss';

const reduxFormName: string = "reduxForms.trialGroupItem";

interface TrialGroupItemCreateVersionProps {
    currentTrialGroupItem: Dtos.TrialGroupItem;
    trialGroupId?: number;
    form?: Dtos.TrialGroupItem & IRegistrationFormState;
    formState?: Dtos.FormState;
    formProperties?: Dtos.FormProperty[];
    reduxFormState?: ITrialGroupItemReduxFormState;
    trialGroupItem?: Dtos.TrialGroupItem;
    lookups?: Dtos.Lookup[];
    loadingTrialGroupItem?: boolean;
    savingTrialGroupItem?: boolean;
    creatingTrialGroupItem?: boolean;
    createTrialGroupItemSuccess?: boolean;
    validationFailures?: Dtos.ResponseError[];
    onClose?: Function;
    clearTrialGroupItem?: (propertiesToClear?: string[]) => void;
    saveTrialGroupItem?: (formData: Dtos.TrialGroupItem, collectionFormData: Dtos.Collection) => void;
    createTrialGroupItem?: (trialGroupId: number) => void;
    loadForm?: (model: string, value: any) => ModelAction;
    changeForm?: (model: string, value: any, options?: ChangeOptions) => ModelAction;
}

const TrialGroupItemCreateVersion: FC<TrialGroupItemCreateVersionProps> = ({
    currentTrialGroupItem,
    trialGroupId,
    form,
    formState,
    formProperties,
    trialGroupItem,
    lookups,
    loadingTrialGroupItem,
    savingTrialGroupItem,
    creatingTrialGroupItem,
    createTrialGroupItemSuccess,
    validationFailures,
    onClose,
    clearTrialGroupItem,
    saveTrialGroupItem,
    createTrialGroupItem,
    loadForm,
    changeForm
}) => {

    // Create GroupItem 
    useEffect(() => {
        if (!trialGroupItem && trialGroupId) {
            createTrialGroupItem(trialGroupId);
        }
    }, [trialGroupItem, trialGroupId, createTrialGroupItem]);

    // Load Form
    useEffect(() => {
        if (!creatingTrialGroupItem && createTrialGroupItemSuccess) {
            loadReduxForm();
        }
    }, [creatingTrialGroupItem, createTrialGroupItemSuccess, loadForm]);

    // Cleanup effect to clear the group item on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupItem();
        };
    }, [clearTrialGroupItem]);

    const handleOnSubmit = (data: Dtos.TrialGroupItem, currentTrialGroupItem: Dtos.TrialGroupItem) => {
        if (!data.version) {
            handleOnSubmitFailed(data);
            return;
        }

        if (data) {
            const changes: Dtos.TrialGroupItem = { 
                trialGroupId: currentTrialGroupItem.trialGroupId, 
                trialGroupItemTemplateId: currentTrialGroupItem.trialGroupItemTemplateId, 
                active: true 
            } as Dtos.TrialGroupItem;
            saveTrialGroupItem({ ...data, ...changes }, null);
        }
        onCancel();
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupItem) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    const onCancel = () => {
        if (onClose) { onClose() }
    }

    const loadReduxForm = () => {

        let extraData: Dtos.TrialGroupItem = { version: undefined } as Dtos.TrialGroupItem;

        loadForm(reduxFormName, Object.assign({ ...trialGroupItem }, extraData));
    }

    const renderContent = useMemo(() => {
        const formDisabled: boolean = savingTrialGroupItem || (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            version: {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled,
                validators: {
                    required: value => value != undefined && value != null && value?.length > 0,
                },
            },
        };

        return (
            <div>
                <Form 
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val, currentTrialGroupItem)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {
                        renderFormControls(form, "trialGroupItem", formComponents, lookups, validationFailures, formProperties, changeForm)
                    }
                    <div className="mt-4 mb-4 text-right">
                        <button 
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button onClick={onCancel} className="btn btn-primary mr-2">Cancel</button>
                    </div>
                </Form>
            </div>
        );
    }, [
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        currentTrialGroupItem
    ]);

    return (
        <SubPageLayout 
            loading={loadingTrialGroupItem}
        >
            { renderContent }
        </SubPageLayout>
    ); 
}

const mapStateToProps = (state: ITmdState, ownProps: TrialGroupItemCreateVersionProps): Partial<TrialGroupItemCreateVersionProps> => {

    let trialGroupItem: Dtos.TrialGroupItem | undefined = !(state.trialGroupItem.formData instanceof Array) ? state.trialGroupItem.formData : undefined;
    
    return {
        currentTrialGroupItem: ownProps.currentTrialGroupItem,
        trialGroupId: ownProps.trialGroupId,
        trialGroupItem: trialGroupItem!,
        loadingTrialGroupItem: state.trialGroupItem.loadState && state.trialGroupItem.loadState.status == RequestState.Pending,
        savingTrialGroupItem: state.trialGroupItem.saveState && state.trialGroupItem.saveState.status == RequestState.Pending,
        creatingTrialGroupItem: state.trialGroupItem.createState && state.trialGroupItem.createState.status == RequestState.Pending,
        createTrialGroupItemSuccess: state.trialGroupItem.createState && state.trialGroupItem.createState.status == RequestState.Success,
        lookups: state.trialGroupItem.lookups,
        form: state.reduxForms.trialGroupItem,
        formState: state.trialGroupItem.formState,
        formProperties: state.trialGroupItem.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupItem,
        validationFailures: state.trialGroupItem.validationFailures,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupItemCreateVersionProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        clearTrialGroupItem: bindActionCreators(TrialGroupItemActions.Clear, dispatch),
        saveTrialGroupItem: bindActionCreators(TrialGroupItemActions.SaveTrialGroupItem, dispatch),
        createTrialGroupItem: bindActionCreators(TrialGroupItemActions.CreateTrialGroupItem, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupItemCreateVersion);
